import { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Plus, Edit, Trash2, ArrowLeft, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';

interface CarBrand {
  id: number;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function CarBrandsSettings() {
  const { t } = useLanguage();
  const [brands, setBrands] = useState<CarBrand[]>([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [editBrand, setEditBrand] = useState<CarBrand | null>(null);
  const [editName, setEditName] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteDeleting, setDeleteDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/brands');
      setBrands(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!addName.trim()) return;
    setAddSubmitting(true);
    try {
      await api.post('/api/brands', { name: addName.trim(), active: true });
      setAddName('');
      setAddOpen(false);
      fetchBrands();
    } catch (error) {
      console.error(error);
    } finally {
      setAddSubmitting(false);
    }
  };

  const openEdit = (brand: CarBrand) => {
    setEditBrand(brand);
    setEditName(brand.name);
  };

  const handleUpdate = async () => {
    if (editBrand == null || !editName.trim()) return;
    setEditSubmitting(true);
    try {
      await api.put(`/api/brands/${editBrand.id}`, { name: editName.trim(), active: editBrand.active });
      setEditBrand(null);
      setEditName('');
      fetchBrands();
    } catch (error) {
      console.error(error);
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId == null) return;
    setDeleteDeleting(true);
    try {
      await api.delete(`/api/brands/${deleteId}`);
      setDeleteId(null);
      fetchBrands();
    } catch (error) {
      console.error(error);
    } finally {
      setDeleteDeleting(false);
    }
  };

  const brandToDelete = deleteId != null ? brands.find((b) => b.id === deleteId) : null;
  const filteredBrands = brands.filter((b) =>
    b.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/settings">
              <Button variant="outline" size="sm" className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('settings.back')}
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('settings.brandsManagement')}</h1>
              <p className="text-slate-500 text-sm mt-0.5">{t('settings.brandsDescription')}</p>
            </div>
          </div>
          <Button onClick={() => setAddOpen(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="h-4 w-4 mr-2" />
            {t('settings.addBrand')}
          </Button>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardContent className="p-6">
            {brands.length > 0 && (
              <div className="relative mb-4 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={t('settings.search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 rounded-lg border-slate-200"
                />
              </div>
            )}
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-10 w-full rounded-lg" />
                ))}
              </div>
            ) : brands.length === 0 ? (
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-12 text-center">
                <p className="text-slate-500">{t('settings.noBrandsYet')}</p>
              </div>
            ) : filteredBrands.length === 0 ? (
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-12 text-center">
                <p className="text-slate-500">{t('settings.search')} — {t('settings.noSearchResults')}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('settings.brandName')}</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('settings.created')}</TableHead>
                    <TableHead className="w-[120px] text-right text-slate-600 font-medium text-sm">{t('settings.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBrands.map((brand) => (
                    <TableRow key={brand.id} className="hover:bg-slate-50">
                      <TableCell className="text-sm text-slate-700 font-medium">{brand.name}</TableCell>
                      <TableCell className="text-sm text-slate-500 tabular-nums">
                        {new Date(brand.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800"
                            onClick={() => openEdit(brand)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => setDeleteId(brand.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Brand Dialog */}
      <Dialog open={addOpen} onOpenChange={(open) => !addSubmitting && setAddOpen(open)}>
        <DialogContent className="sm:max-w-md border-slate-200">
          <DialogHeader>
            <DialogTitle>{t('settings.addBrand')}</DialogTitle>
            <DialogDescription>{t('settings.brandsDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-slate-700">{t('settings.brandName')}</Label>
              <Input
                placeholder={t('settings.brandName')}
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                className="rounded-lg border-slate-200"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setAddOpen(false)} disabled={addSubmitting} className="border-slate-200">
              {t('settings.cancel')}
            </Button>
            <Button type="button" onClick={handleCreate} disabled={!addName.trim() || addSubmitting} className="bg-slate-900 hover:bg-slate-800">
              {t('settings.addBrand')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Brand Dialog */}
      <Dialog open={editBrand != null} onOpenChange={(open) => !open && !editSubmitting && setEditBrand(null)}>
        <DialogContent className="sm:max-w-md border-slate-200">
          <DialogHeader>
            <DialogTitle>{t('settings.editBrand')}</DialogTitle>
            <DialogDescription>{t('settings.brandsDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-slate-700">{t('settings.brandName')}</Label>
              <Input
                placeholder={t('settings.brandName')}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="rounded-lg border-slate-200"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setEditBrand(null)} disabled={editSubmitting} className="border-slate-200">
              {t('settings.cancel')}
            </Button>
            <Button type="button" onClick={handleUpdate} disabled={!editName.trim() || editSubmitting} className="bg-slate-900 hover:bg-slate-800">
              {t('settings.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Brand Dialog */}
      <Dialog open={deleteId != null} onOpenChange={(open) => !open && !deleteDeleting && setDeleteId(null)}>
        <DialogContent className="sm:max-w-md border-slate-200">
          <DialogHeader>
            <DialogTitle>{t('settings.deleteBrand')}</DialogTitle>
            <DialogDescription>
              {brandToDelete ? `"${brandToDelete.name}" ${t('settings.confirmDelete')}` : t('settings.confirmDelete')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setDeleteId(null)} disabled={deleteDeleting} className="border-slate-200">
              {t('settings.cancel')}
            </Button>
            <Button type="button" variant="destructive" disabled={deleteDeleting} className="bg-red-600 hover:bg-red-700" onClick={handleDelete}>
              {deleteDeleting ? t('settings.deleting') : t('settings.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
